'use client';

import {
  SiAmazonwebservices,
  SiDocker,
  SiFigma,
  SiGo,
  SiKotlin,
  SiLangchain,
  SiNextdotjs,
  SiPostgresql,
  SiPython,
  SiReact,
  SiRedis,
  SiSpringboot,
  SiTypescript,
} from 'react-icons/si';
import { motion, useReducedMotion } from 'motion/react';

import { Icons } from '@/components/icons';
import { PageHeading } from '@/components/page-heading';
import { AppImage } from '@/components/layout/app-image';
import { experiencesData } from '@/lib/data';

const spring = { type: 'spring' as const, bounce: 0, duration: 0.5 };

const fadeInAnimationVariants = {
  initial: { opacity: 0, y: 40 },
  animate: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      bounce: 0,
      duration: 0.5,
      delay: 0.05 * index,
    },
  }),
};

const skillsData = [
  { name: 'Kotlin', icon: <SiKotlin size={48} className="text-primary" /> },
  {
    name: 'Spring Boot',
    icon: <SiSpringboot size={48} className="text-primary" />,
  },
  {
    name: 'TypeScript',
    icon: <SiTypescript size={48} className="text-primary" />,
  },
  { name: 'Python', icon: <SiPython size={48} className="text-primary" /> },
  { name: 'Golang', icon: <SiGo size={48} className="text-primary" /> },
  { name: 'React', icon: <SiReact size={48} className="text-primary" /> },
  { name: 'Next.js', icon: <SiNextdotjs size={48} className="text-primary" /> },
  {
    name: 'PostgreSQL',
    icon: <SiPostgresql size={48} className="text-primary" />,
  },
  { name: 'Redis', icon: <SiRedis size={48} className="text-primary" /> },
  { name: 'Docker', icon: <SiDocker size={48} className="text-primary" /> },
  {
    name: 'AWS',
    icon: <SiAmazonwebservices size={48} className="text-primary" />,
  },
  {
    name: 'LangChain',
    icon: <SiLangchain size={48} className="text-primary" />,
  },
  { name: 'Figma', icon: <SiFigma size={48} className="text-primary" /> },
];

export function AboutPageContent() {
  const reducedMotion = useReducedMotion() ?? false;
  const motionProps = reducedMotion
    ? {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        transition: { duration: 0.2 },
      }
    : {
        initial: { opacity: 0, scale: 0.8 },
        animate: { opacity: 1, scale: 1 },
        transition: spring,
      };

  return (
    <div className="mx-auto flex min-h-[60vh] max-w-6xl flex-col items-center px-6">
      <section id="about" className="my-16 w-full max-w-4xl">
        <PageHeading>About Me</PageHeading>
        <div className="mb-20 flex flex-col items-center gap-8 md:flex-row md:items-start">
          <motion.div
            {...motionProps}
            className="relative size-52 overflow-hidden rounded-2xl sm:size-72 md:size-80"
          >
            <AppImage
              src="/images/profile.png"
              alt="Profile photo"
              fill
              className="object-cover"
            />
          </motion.div>

          <motion.div
            initial={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={
              reducedMotion ? { duration: 0.2 } : { ...spring, delay: 0.2 }
            }
            className="flex-1 space-y-4 text-center text-sm sm:text-left sm:text-lg"
          >
            <p>
              Hi, I&apos;m Alfa, a Fullstack AI Engineer based in Jakarta
              (GMT+7), targeting Singapore remote. For 5+ years I&apos;ve turned
              complex business problems into simple, reliable products.
            </p>
            <p>
              On the backend I work with Kotlin &amp; Java (Spring Boot),
              Golang, and Node.js: microservices, payment integrations, and
              platforms that have to stay up. On the frontend I ship full stack
              products with TypeScript, React, and Next.js.
            </p>
            <p>
              Lately I&apos;ve been building AI native systems: agentic LLM
              workflows, hybrid RAG, and tool calling loops, including Incident
              Brain in production, which cut root cause diagnosis from hours to
              minutes. Stack includes PostgreSQL, Redis, Docker, AWS, and
              observability via Langfuse.
            </p>
            <p>
              I&apos;m a visual thinker and a fast learner. I sketch systems
              first with diagrams, Figma, and shared models, then build. That
              habit is how I moved from analysis into shipping AI without losing
              the business context.
            </p>
            <div className="my-12 flex items-center justify-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <p>
              Outside of engineering: running, films, and coffee. Primary goal
              is a full time Singapore remote role, though I&apos;m also open to
              freelance, part time, and contract work when the impact is real.
            </p>
            <p>
              If that sounds useful,{' '}
              <span className="text-brand font-bold">let&apos;s talk.</span>
            </p>
          </motion.div>
        </div>

        <div className="mb-20">
          <motion.h2
            className="mb-8 text-center text-3xl font-bold"
            variants={fadeInAnimationVariants}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            Skill Sets
          </motion.h2>

          <div className="mt-10 flex w-full flex-wrap items-center justify-center gap-2 sm:gap-10 sm:px-0 md:mt-14 lg:justify-between">
            {skillsData.map(({ icon }, index) => (
              <motion.div
                key={index}
                variants={fadeInAnimationVariants}
                initial="initial"
                whileInView="animate"
                viewport={{ once: true }}
                custom={index}
                className="flex items-center justify-center p-4"
              >
                {icon}
              </motion.div>
            ))}
          </div>
        </div>

        <div className="mb-20">
          <motion.h2
            className="mb-8 text-center text-3xl font-bold"
            variants={fadeInAnimationVariants}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            Experience
          </motion.h2>
          <div className="relative max-w-screen-md">
            {experiencesData.map(
              ({ id, title, company, highlights, period, technologies }) => (
                <div
                  key={id}
                  className="not-last:pb-12 relative pl-8 [&:not(:last-child)]:pb-10"
                >
                  <div className="bg-muted absolute left-0 top-2.5 h-full w-[2px] group-first:top-6 group-first:h-[calc(100%-24px)]">
                    <div className="border-primary bg-background absolute left-[-5px] top-0 size-3 rounded-full border-2" />
                  </div>
                  <motion.div
                    initial={
                      reducedMotion ? { opacity: 0 } : { opacity: 0, y: 60 }
                    }
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={
                      reducedMotion
                        ? { duration: 0.2 }
                        : { ...spring, delay: 0.1 }
                    }
                    viewport={{ once: true }}
                    className="space-y-3"
                  >
                    <div>
                      <h3 className="text-xl font-medium">{title}</h3>
                      <p className="text-muted-foreground mt-0.5 text-sm font-medium">
                        {company}
                      </p>
                      <div className="mt-1 flex items-center gap-2 text-sm">
                        <Icons.calendar className="size-4" />
                        <span>{period}</span>
                      </div>
                    </div>
                    <ul className="text-muted-foreground space-y-2 text-sm leading-relaxed sm:text-base">
                      {highlights.map((item) => (
                        <li key={item} className="flex gap-2.5">
                          <span
                            aria-hidden
                            className="bg-brand/70 mt-2 size-1.5 shrink-0 rounded-full"
                          />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="flex flex-wrap gap-2">
                      {technologies.map((tech) => (
                        <div
                          key={tech}
                          className="rounded-full border px-3 py-1 text-sm"
                        >
                          {tech}
                        </div>
                      ))}
                    </div>
                  </motion.div>
                </div>
              )
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
